export const BIOGRAPHY_GUIDE = `
# Personal Biography Guide

Your Personal Biography is the foundation of VoiceThera's AI personalization. This guide explains how to create a powerful biography that transforms your wellness sessions.

## Why Biography Matters

### The Personalization Engine

VoiceThera's AI uses your biography to:

- **Understand your context** - Your environment, relationships, challenges
- **Reference your specific life** - Real places, people, and situations
- **Connect to your strengths** - Past victories and coping mechanisms
- **Align with your goals** - Personal aspirations and values
- **Speak your language** - Resonate with your unique personality

### The Difference It Makes

**Without Biography (Generic):**
> "Take a deep breath. Imagine a peaceful place. You are capable. You can handle stress."

**With Biography (Personalized):**
> "You're sitting at your kitchen table in Denver, coffee in hand, thinking about the presentation you crushed last year when you thought you couldn't. That's who you are - someone who shows up even when it's scary. Your daughter sees that in you. Your hiking buddy sees that in you. That mountain you climbed last summer? That's the same strength you're bringing to today's challenge."

## Biography Structure

Your biography is organized into **8 key areas**. Each area helps AI understand a different dimension of your life.

### 1. Location & Environment

**What to include:**
- City/town where you live
- Type of environment (urban, suburban, rural, mountains, coast)
- Climate and how it affects you
- Favorite local spots or landmarks
- How you feel about where you live

**Why it matters:**
AI can ground sessions in your actual environment, making visualizations and metaphors more powerful.

**Example:**
> "I live in Portland, Oregon, surrounded by evergreen forests and constant rain. The gray skies can be heavy, but I love the smell of wet earth. I run along the Willamette River most mornings and feel most alive hiking in Forest Park. The city's creative energy matches my personality, but sometimes the gloom gets to me."

### 2. Family & Relationships

**What to include:**
- Partner/spouse (name, relationship dynamics)
- Children (names, ages, key characteristics)
- Parents and siblings (living situation, relationship quality)
- Close friends (who they are, what they mean to you)
- Pets (names, species, personality)

**Why it matters:**
AI references these people to motivate you, remind you of your support system, and connect challenges to people who care about you.

**Example:**
> "I'm married to Sarah (12 years), who's a nurse and my rock. Our kids are Maya (9, creative and sensitive) and Liam (6, wild energy, obsessed with dinosaurs). My mom lives nearby and helps with childcare - we're close. My best friend Jake and I have been tight since college; he's the one I call when things get real. And there's Bruno, our neurotic rescue dog who somehow knows when I need comfort."

### 3. Current Challenges & Stressors

**What to include:**
- Work stress and job pressures
- Relationship conflicts or tensions
- Health concerns (physical or mental)
- Financial worries
- Major life transitions
- Daily hassles that wear you down

**Why it matters:**
AI directly addresses these challenges in sessions, providing targeted therapeutic support.

**Example:**
> "I'm drowning in my marketing director role - my boss micromanages everything and the team is understaffed. I'm working 60-hour weeks and missing my kids' stuff. Sarah and I barely talk anymore beyond logistics. I've gained 20 pounds this year and my back hurts constantly from sitting. Money is tight because of daycare costs. I feel like I'm failing at everything."

### 4. Past Victories & Strengths

**What to include:**
- Challenges you've overcome in the past
- Moments you're proud of
- Skills and talents
- Times you proved yourself capable
- Compliments others give you
- What you're good at (even small things)

**Why it matters:**
AI reminds you of your proven capabilities when you're doubting yourself. These become anchors during difficult moments.

**Example:**
> "I quit drinking 3 years ago and haven't touched alcohol since - that took serious willpower. I built my own deck last summer without help (YouTube university). I delivered a TEDx talk despite crippling stage fright. People say I'm a great listener and give solid advice. I can cook a mean lasagna. I once hiked 15 miles with a twisted ankle because I refused to quit."

### 5. Personal Goals & Aspirations

**What to include:**
- Career goals and professional dreams
- Personal development objectives
- Health and fitness targets
- Relationship improvements you want
- Creative projects or hobbies to explore
- Long-term life vision
- What success looks like for you

**Why it matters:**
AI connects daily challenges to your bigger purpose, helping you see setbacks as part of your growth journey.

**Example:**
> "I want to move into a VP role within 2 years - I know I'm ready. I'm training for my first marathon in 6 months. I want to be more present with my kids - really present, not phone-in-hand present. Sarah and I need to reconnect romantically; we used to have so much fun together. I've always wanted to write a book about my experience quitting drinking. Big picture: I want to be someone my kids admire, not someone who just survived parenthood."

### 6. Favorite Places & Spaces

**What to include:**
- Where you feel most peaceful
- Where you feel most alive
- Where you go to think or reset
- Indoor and outdoor favorite spots
- Places with special meaning

**Why it matters:**
AI uses these locations for visualization exercises and grounding techniques in sessions.

**Example:**
> "My happy place is the summit of Silver Star Mountain - 360-degree views, total silence except wind and birds. I also love my garage workshop where I build furniture; sawdust and wood smell = therapy. Our back porch at sunrise with coffee, watching the neighborhood wake up. Powell's Books downtown - I could get lost in there for hours. And honestly, my car during my commute when I blast music and sing badly."

### 7. Hobbies & Interests

**What to include:**
- Creative outlets (art, music, writing)
- Physical activities (sports, hiking, yoga)
- Intellectual pursuits (reading, learning, puzzles)
- Social activities (gaming, clubs, volunteering)
- What you do to unwind
- What makes you lose track of time

**Why it matters:**
AI references these activities as coping mechanisms and sources of joy to reconnect with.

**Example:**
> "I'm a woodworker - building furniture with my hands is meditative. I run 4-5 times a week, training for that marathon. I read fantasy novels to escape (Brandon Sanderson is my hero). I play D&D with Jake's group every other Thursday - total nerd and proud of it. I love cooking elaborate meals on weekends when I have time. I used to play guitar but haven't picked it up in 2 years - I miss it."

### 8. Core Values & What Matters Most

**What to include:**
- What you stand for
- What you won't compromise on
- What makes you angry or passionate
- What you want to be remembered for
- What "integrity" means to you
- How you want to show up in the world

**Why it matters:**
AI helps you reconnect with your values when you feel lost or compromised.

**Example:**
> "Integrity is everything to me - I can't stand dishonesty or corner-cutting. Being a present, engaged parent is non-negotiable; I refuse to be an absent dad. I believe in working hard but not sacrificing family for career. Loyalty matters - I show up for people I care about. Authenticity over popularity. Kindness over being right. I want my kids to remember me as someone who tried, who wasn't perfect, but who loved them fiercely and taught them to be good humans."

## Biography Quality Scoring

VoiceThera automatically analyzes your biography and provides a **Quality Score** (0-100%).

### Score Breakdown

- **0-30%: Minimal** - Very basic information, needs significant expansion
- **31-50%: Basic** - Some useful details, but AI will struggle to personalize deeply
- **51-70%: Good** - Solid foundation, AI can create meaningful personalized content
- **71-85%: Excellent** - Rich, detailed biography enabling powerful personalization
- **86-100%: Outstanding** - Exceptional detail enabling transformative session experiences

### What Impacts Your Score

**Increases Score:**
- Specific names (people, places, pets)
- Detailed descriptions with sensory information
- Concrete examples of challenges and victories
- Emotional honesty and vulnerability
- Multiple dimensions covered (work, family, health, etc.)
- 500+ words of meaningful content

**Lowers Score:**
- Vague statements ("I'm stressed", "things are hard")
- One or two-word responses
- Missing sections
- Generic descriptions
- Under 300 words total
- No specific details AI can reference

### Improving Your Score

Use the recommendations provided by the scoring system:

- **"Add more detail about X"** - Expand that section with specific examples
- **"Include names"** - Replace "my wife" with "Sarah", "my friend" with "Jake"
- **"Describe how you feel"** - Add emotional context to factual statements
- **"Provide concrete examples"** - Replace "I'm stressed at work" with specific situations

## Smart Writing Assistant

The Smart Writing Assistant helps you create a powerful biography through AI-powered suggestions.

### How to Use It

1. **Click "Get Suggestions" button** in any biography section
2. **Review AI-generated prompts** designed to unlock deeper details
3. **Answer prompts in the text field** - be honest and specific
4. **Save your responses** - AI will use these details in sessions

### Example Prompts

**For Current Challenges:**
- "Describe a recent moment at work that made you want to quit"
- "What specific behavior from others triggers your anxiety?"
- "When was the last time you cried about this? What happened?"

**For Past Victories:**
- "Tell me about a time you surprised yourself with your strength"
- "What challenge did you overcome that you're most proud of?"
- "When have you been told 'I don't know how you did that'?"

**For Relationships:**
- "Describe your partner in 5 words, then explain each one"
- "What do your kids not know about you that you wish they understood?"
- "Who would you call at 3am if everything fell apart?"

### Best Practices

**Do:**
- Answer as if talking to a close friend
- Include sensory details (sights, sounds, smells, physical sensations)
- Mention specific dates, places, names
- Express emotions honestly (anger, fear, pride, shame)
- Write in your natural voice, not formal language

**Don't:**
- Censor yourself or downplay struggles
- Write what you think "sounds good"
- Use generic or cliched language
- Leave sections blank (even "I don't have X" is useful context)
- Worry about grammar or perfect writing

## Privacy & Security

### What Happens to Your Biography

- **Stored securely** - Encrypted in our database
- **Never shared** - Only used to generate your personal sessions
- **Not sold or disclosed** - Your data is not shared with third parties
- **You control access** - Edit or delete anytime
- **AI uses it ephemerally** - Generated session content doesn't store your full biography

### Sensitive Information Guidelines

You can include personal information, but consider:

- **Names** - Using first names only is fine if you're concerned
- **Specific addresses** - "Portland area" vs. exact street address
- **Medical details** - Include health context but consult healthcare providers for serious conditions
- **Workplace identification** - "My boss at my marketing job" vs. "John Smith at Acme Corp"
- **Trauma details** - Include what's relevant for therapeutic work, but VoiceThera is not a replacement for professional trauma therapy

## Editing Your Biography

### When to Update

Update your biography when:

- Major life changes occur (job change, move, relationship changes)
- You overcome a significant challenge (add to "Past Victories")
- New stressors emerge (update "Current Challenges")
- Your goals evolve (update "Personal Goals")
- Sessions feel less relevant (usually means biography is outdated)
- Every 3-6 months for general maintenance

### How to Edit

1. Navigate to **Profile → Personal Biography**
2. Click **"Edit Biography"**
3. Update any sections that need changes
4. Check your Quality Score after edits
5. Click **"Save Changes"**
6. Your next session will use the updated biography

### Version History

VoiceThera keeps your biography history so you can:

- See how your challenges have evolved
- Track your growth over time
- Revert to previous versions if needed

## Biography Optimization Tips

### 1. Write Like You're Talking to a Therapist

Imagine you're in a therapy session explaining your life to someone who genuinely wants to understand you. Write with that same honesty and depth.

### 2. Show, Don't Tell

**Telling:** "I'm stressed at work"
**Showing:** "Last Tuesday my boss called me into his office and told me my project wasn't 'strategic enough' after I'd worked on it for 3 months. I drove home in silence, too angry to even listen to music, and snapped at Sarah when she asked how my day went."

### 3. Include the "Why"

Don't just state facts - explain why they matter to you.

"I have two kids" vs. "I have two kids, Maya and Liam, and being a present dad is the most important thing in my life because my own father was never around"

### 4. Use All Your Senses

When describing places, people, or experiences, engage all senses:

- **Sight** - What do you see?
- **Sound** - What do you hear?
- **Smell** - Any smells associated with this?
- **Touch** - Physical sensations?
- **Emotion** - How does this make you feel?

### 5. Balance Challenges and Strengths

AI needs both struggles and strengths to create balanced, empowering sessions. Don't focus only on problems - celebrate your wins too!

### 6. Update After Powerful Sessions

After a particularly impactful session, update your biography to reflect any insights, breakthroughs, or shifts in perspective.

### 7. Be Specific About Time

Use specific timeframes to give AI context:

- "3 years ago I quit drinking"
- "Last month I got passed over for promotion"
- "This morning I had a panic attack before work"
- "For the past 6 months I've been training for a marathon"

## Biography and Session Quality

### The Biography-Session Connection

Your biography quality directly impacts session quality:

**High-Quality Biography (70%+):**
- Sessions reference your specific people, places, challenges
- Therapeutic language resonates deeply with your exact situation
- Metaphors and examples drawn from your actual life
- Motivation tied to your real goals and relationships
- Past victories used as evidence of your capabilities

**Low-Quality Biography (Under 50%):**
- Sessions feel more generic and less personal
- References to "people in your life" instead of specific names
- Generic challenges instead of your actual struggles
- Weaker emotional resonance
- Missed opportunities for deep personalization

### Testing Session Quality

Generate a test session after completing your biography and listen for:

- Does it mention specific names, places, or situations from your biography?
- Does the language feel like it was written specifically for you?
- Are your actual challenges addressed, not generic stress?
- Do you feel understood and seen by the session content?
- Are you motivated by references to your real goals and relationships?

If sessions feel generic, your biography likely needs more detail and specificity.

## Common Biography Mistakes

### 1. Too Short

A 50-word biography can't provide enough context. Aim for 500-1000+ words across all sections.

### 2. Too Vague

"I'm stressed" tells AI nothing.
"My boss micromanages me, I'm behind on three projects with impossible deadlines, and I haven't slept more than 5 hours in a week" gives AI actionable context.

### 3. All Challenges, No Strengths

If you only describe problems, AI can't remind you of your proven capabilities. Balance struggles with victories.

### 4. No Names or Specific Details

"My wife", "my friend", "my town" are generic.
"Sarah", "Jake", "Portland" are specific and powerful.

### 5. Writing What You Think Sounds Good

AI doesn't judge you - it needs truth to help you. Raw, honest details create better sessions than polished but empty prose.

### 6. Neglecting Physical/Environmental Details

Where you live, what you do physically, your health context - these grounding details make visualizations and body-based practices more powerful.

### 7. Forgetting Your Wins

You've overcome challenges before. Including past victories gives AI evidence to remind you of your strength.

## Advanced Biography Features

### Biography Variables

VoiceThera extracts key "variables" from your biography that AI uses consistently:

- **Names** - People, pets, places
- **Challenges** - Current stressors
- **Strengths** - Past victories and capabilities
- **Goals** - What you're working toward
- **Values** - What matters most to you

Review your extracted variables to ensure AI captured the most important elements.

### Anchor Points

"Anchor points" are powerful memories or truths AI can reference repeatedly:

- The mountain you climbed when you thought you couldn't
- The moment you decided to quit drinking
- Your daughter's laugh when you read her bedtime stories
- The feeling of sawdust on your hands in your workshop

Identify 3-5 anchor points in your biography that represent your strength, joy, or purpose.

### Biography Coaching

Struggling to write your biography? Use the built-in Biography Coach:

1. Click **"Get Biography Coaching"**
2. Answer conversational prompts
3. AI will draft biography sections for you based on your responses
4. Edit and personalize the AI-drafted content
5. Save your enhanced biography

---

**Ready to create powerful sessions?** A detailed biography is your foundation. Take your time, be honest, and watch your sessions transform.

Need help? Browse other guides in the Help Center or contact support at support@voicethera.com
`;
